import { Injectable } from '@angular/core';
import {
    entityKeysByType,
    generatedAt,
    hasEntity,
    hasPage,
    loadEntity,
    pageKeys,
    pageLoaders,
} from 'app/generated/snapshots';

// Small in-memory cache so the same page/entity chunk resolves synchronously
// after the first hit — avoids re-running dynamic import for repeated
// navigations within the same SSR render or SPA session.
@Injectable({
    providedIn: 'root',
})
export class PublicContentSnapshotService {
    readonly generatedAt = generatedAt;
    readonly pageKeys = pageKeys;
    readonly entityKeysByType = entityKeysByType;

    private pageCache = new Map<string, unknown>();
    private entityCache = new Map<string, unknown>();

    hasPage(key: string): boolean {
        return hasPage(key);
    }

    hasEntity(type: string, key: string): boolean {
        return hasEntity(type, key);
    }

    async getPage<T>(key: string): Promise<T | null> {
        if (this.pageCache.has(key)) {
            return this.pageCache.get(key) as T;
        }
        const loader = pageLoaders[key];
        if (!loader) return null;
        try {
            const value = (await loader()) as T;
            this.pageCache.set(key, value);
            return value;
        } catch (error) {
            console.error(`[snapshot] page load failed key=${key}:`, error);
            return null;
        }
    }

    async getEntity<T>(type: string, key: string): Promise<T | null> {
        const cacheKey = `${type}:${key}`;
        if (this.entityCache.has(cacheKey)) {
            return this.entityCache.get(cacheKey) as T;
        }
        const loader = loadEntity(type, key);
        if (!loader) return null;
        try {
            const value = (await loader) as T;
            this.entityCache.set(cacheKey, value);
            return value;
        } catch (error) {
            console.error(`[snapshot] entity load failed ${type}/${key}:`, error);
            return null;
        }
    }
}
